import { Group, Paper, Select, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { TicketZeitraum, Vorgangstyp } from "@msc/shared";

import { useKpiTicketMonate, useKpiTicketWiedereroeffnung } from "../../../hooks/useKpiTickets.js";
import { CHART_COLORS } from "../../../theme.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./TicketAuswahlModal.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";
import { MONAT_KURZ, monatsGrenzen, prozent, RECHTE_ACHSE_BREITE } from "./ticketDarstellung.js";

/**
 * SR-08: Stimmt die Erstlösung? Quote im Zeitraum als Zahl, darunter der
 * Monatstrend auf der Jahresachse der Seite (fest, nicht über den Zeitraum).
 * Der Klick auf die Quote zeigt beide Listen: wieder geöffnet und gelöst.
 */
export function WiedereroeffnungKarte({ segmentId, year }: { segmentId: string; year: number }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [zeitraum, setZeitraum] = useState<TicketZeitraum | null>(null);
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const quote = useKpiTicketWiedereroeffnung(segmentId, year, zeitraum, vorgangstyp);
  const monate = useKpiTicketMonate(segmentId, year, vorgangstyp);

  const monatsName = (monat: string) => MONAT_KURZ[Number(monat.slice(5, 7)) - 1]!;
  const quoteDes = (m: { offen: number | null; geloest: number; wiedereroeffnet: number }) =>
    m.offen === null || m.geloest === 0 ? undefined : (m.wiedereroeffnet / m.geloest) * 100;
  const zeigeMonat = (monat: string) => {
    const g = monatsGrenzen(monat);
    setAuswahl({
      titel: t("pages.kpi.tickets.monate.auswahlTitel", { monat: `${monatsName(monat)} ${monat.slice(0, 4)}` }),
      abschnitte: [
        { titel: t("pages.kpi.tickets.wiedereroeffnet"), abfrage: { quelle: "wiedereroeffnet", ...g, vorgangstyp } },
        { titel: t("pages.kpi.tickets.geloest"), abfrage: { quelle: "geloest", ...g, vorgangstyp } },
      ],
    });
  };

  const trend = (monate.data?.monate ?? []).map((m) => ({
    name: monatsName(m.monat),
    monat: m.monat,
    waehlbar: m.offen !== null,
    quote: quoteDes(m),
  }));

  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf titel={t("pages.kpi.tickets.wieder.titel")} spiegel={quote.data?.spiegel} vorgangstyp={vorgangstyp} onVorgangstyp={setVorgangstyp} zeitraum={zeitraum} onZeitraum={setZeitraum} />
      {quote.isLoading ? (
        <Skeleton height={200} />
      ) : quote.isError || !quote.data ? (
        <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text>
      ) : (
        <>
          <Group gap="lg" mb="sm">
            <UnstyledButton
              aria-label={t("pages.kpi.tickets.wieder.knopf", { wieder: quote.data.wiedereroeffnet, geloest: quote.data.geloest })}
              onClick={() =>
                setAuswahl({
                  titel: t("pages.kpi.tickets.wieder.titel"),
                  abschnitte: [
                    { titel: t("pages.kpi.tickets.wiedereroeffnet"), abfrage: { quelle: "wiedereroeffnet", ...quote.data!.zeitraum, vorgangstyp } },
                    { titel: t("pages.kpi.tickets.geloest"), abfrage: { quelle: "geloest", ...quote.data!.zeitraum, vorgangstyp } },
                  ],
                })
              }
            >
              <Text size="xl" fw={700}>{prozent(quote.data.quote)}</Text>
              <Text size="xs" c="dimmed">{t("pages.kpi.tickets.wieder.zaehler", { wieder: quote.data.wiedereroeffnet, geloest: quote.data.geloest })}</Text>
            </UnstyledButton>
          </Group>
          <Select
            size="xs"
            mb="xs"
            aria-label={t("pages.kpi.tickets.wieder.monatAnsehen")}
            placeholder={t("pages.kpi.tickets.wieder.monatAnsehen")}
            value={null}
            onChange={(v) => v && zeigeMonat(v)}
            data={(monate.data?.monate ?? []).filter((m) => m.offen !== null).map((m) => ({
              value: m.monat,
              label: `${monatsName(m.monat)} – ${prozent(quoteDes(m) ?? null)}`,
            }))}
            style={{ maxWidth: 320 }}
          />
          <ResponsiveContainer width="100%" height={180}>
            <LineChart
              data={trend}
              onClick={(e) => {
                const p = (e as { activePayload?: Array<{ payload?: { monat?: string; waehlbar?: boolean } }> } | null)?.activePayload?.[0]?.payload;
                if (p?.monat && p.waehlbar) zeigeMonat(p.monat);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis unit=" %" />
              <YAxis yAxisId="platz" orientation="right" width={RECHTE_ACHSE_BREITE} tick={false} axisLine={false} />
              <Tooltip formatter={(v) => prozent(Number(v))} />
              <Line dataKey="quote" name={t("pages.kpi.tickets.wieder.trend")} stroke={CHART_COLORS.ist} dot type="monotone" />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
      <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={quote.data?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
    </Paper>
  );
}
